import { BRAND } from '../../constants'
import { insertDynamicText } from '../../controllers/helpers'
import { useEffect, useRef, useState } from 'react'
import { useDebouncedWindowSize } from '../../hooks/useDebouncedWindowSize.ts'
import ReactMarkdown from 'react-markdown'
import { overflowComponents } from '../../utilities/markdownOperations'
import { Faq, LongGuide as LongGuideType } from '../../models/types/LongGuide.type'
const { media } = BRAND

type FaqProps = {
  faq_title: string
  faqs: Faq[]
}

const Faqs = ({ faq_title, faqs }: FaqProps) => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  const handleExpand = (rowIndex: number) => {
    if (openFAQ === rowIndex) {
      setOpenFAQ(null)
    } else {
      setOpenFAQ(rowIndex)
    }
  }

  return (
    <section className='faqs'>
      <h2 className='FAQ-title'>{faq_title}</h2>
      <div id='accordion' className='accordion'>
        {faqs.map((faq, i) => (
          <section className='accordion-tab' key={i} id={`section${i}`}>
            <h3 className='title'>
              <button className='row' onClick={() => handleExpand(i)}>
                <span id={`plus${i}`} className='row'>
                  <span>{faq.question}</span> {openFAQ === i ? <span>&#8722;</span> : <span>&#43;</span>}
                </span>
              </button>
            </h3>
            <section className={`answer ${openFAQ === i && 'show'}`}>
              <ReactMarkdown>{faq.answer}</ReactMarkdown>
            </section>
          </section>
        ))}
      </div>
      <style jsx>{`
        .faqs {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          padding: 0 32px;
        }
        .FAQ-title {
          font-weight: 600;
          font-size: 24px;
          margin: 24px 0px;
          line-height: 1.5;
        }
        .accordion {
          width: 100%;
          max-width: 750px;
        }
        .accordion-tab {
          border-top: 1px solid lightgrey;
        }
        .FAQ-section {
          display: flex;
          flex-direction: row;
          background-color: transparent;
          justify-content: space-between;
          align-items: center;
          border: 0px;
          width: 100%;
        }
        .title {
          font-weight: 600;
          font-size: 18px;
          background-color: transparent;
          border: 0px;
          padding: 16px 0px;
        }
        .row {
          cursor: pointer;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
          background: transparent;
          font-size: 18px;
          font-weight: 500;
          text-align: left;
        }
        .answer {
          display: none;
          visibility: hidden;
          padding-bottom: 12px;
          cursor: intial;
        }
        .show {
          display: block;
          visibility: visible;
        }

        @media screen and (max-width: ${media.small}px) {
          .FAQ-title {
            font-size: 18px;
          }
          .title {
            font-size: 16px;
            padding: 12px 0px;
          }
          .faqs {
            padding: 0;
          }
        }
      `}</style>
    </section>
  )
}

type LongGuideProps = {
  longGuide: LongGuideType
  displayLocation: string
  nicheOrEventName: string
}

export const LongGuide = ({ longGuide, displayLocation = '', nicheOrEventName = '' }: LongGuideProps) => {
  let { title, guide, expand_text, close_text, faq_title, faqs } = longGuide || {}
  title = insertDynamicText(title, displayLocation, nicheOrEventName)
  const guideRef = useRef<HTMLDivElement>(null)
  const [showMore, setShowMore] = useState<boolean>(false)
  const [isClamped, setIsClamped] = useState<boolean>(false)

  useEffect(() => {
    setIsClamped(!!guideRef.current && guideRef.current.scrollHeight > guideRef.current.clientHeight)
  }, [setIsClamped])

  const showHide = () => {
    setShowMore((prev) => {
      if (prev) {
        const heading = document.getElementById('guide-heading')
        if (heading) {
          heading.scrollIntoView({ block: 'center' })
        }
      }
      return !prev
    })
  }

  return (
    longGuide && (
      <section className='container'>
        <h2 aria-level={1} id='guide-heading' className='heading'>
          {title}
        </h2>

        <div className={showMore ? 'full' : 'truncated'} ref={guideRef}>
          <ReactMarkdown components={overflowComponents}>{guide}</ReactMarkdown>
        </div>
        {isClamped && (
          <button onClick={showHide} className='read-more'>
            <div className='read-more-font'>{showMore ? close_text : expand_text}</div>
          </button>
        )}
        {faqs.length > 0 && <Faqs {...{ faq_title, faqs }} />}
        <style jsx>{`
          .container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: 0 32px 64px;
            max-width: 800px;
          }
          .hide {
            display: none;
            visibility: hidden;
          }

          .truncated {
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 1;
          }

          .full {
            display: block;
          }

          .heading {
            font-size: 40px;
            font-weight: 600;
            line-height: 52px;
            text-align: center;
            margin: 28px 0px;
          }
          .read-more {
            background: transparent;
            cursor: pointer;
            width: 100%;
            text-align: center;
            margin: 12px;
          }
          .read-more-font {
            font-weight: 600;
            font-size: 18px;
            line-height: 1.7;
          }
          .read-less-font {
            background: transparent;
            cursor: pointer;
            width: 100%;
            text-align: center;
            margin: 12px;
            font-weight: 600;
            font-size: 18px;
            line-height: 1.7;
          }
          @media screen and (max-width: ${media.small}px) {
            .heading {
              font-size: 28px;
              line-height: 40px;
            }
          }
        `}</style>
      </section>
    )
  )
}

export default LongGuide
